import React, { useState, useEffect } from 'react';
import { getCatalogAnalytics } from '../../services/AnalyticsService';
import { getArtistId } from '../../state/ArtistManager.js';

const CatalogAnalytics = () => {
  const [catalogData, setCatalogData] = useState(null);
  const [loading, setLoading] = useState(true);
  const artistId = getArtistId();

  useEffect(() => {
    loadCatalogAnalytics();
  }, [artistId]);

  const loadCatalogAnalytics = async () => {
    try {
      setLoading(true);
      const data = await getCatalogAnalytics(artistId);
      setCatalogData(data);
    } catch (error) {
      console.error('Error loading catalog analytics:', error);
    } finally {
      setLoading(false);
    }
  };

  if (loading) return <div className="p-4">Loading catalog analytics...</div>;

  const { metrics, performance, marketing } = catalogData || {};

  return (
    <div className="space-y-6">
      {/* Core Metrics */}
      <div className="bg-white rounded-lg shadow-md p-6">
        <h3 className="text-xl font-bold mb-4">📊 Catalog Overview</h3>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <div className="text-center">
            <div className="text-2xl font-bold text-blue-600">{metrics?.totalTracks || 0}</div>
            <div className="text-sm text-gray-600">Total Tracks</div>
          </div>
          <div className="text-center">
            <div className="text-2xl font-bold text-green-600">{metrics?.avgViralScore || 0}</div>
            <div className="text-sm text-gray-600">Avg Viral Score</div>
          </div>
          <div className="text-center">
            <div className="text-2xl font-bold text-purple-600">{metrics?.marketingReady || 0}</div>
            <div className="text-sm text-gray-600">Marketing Ready</div>
          </div>
          <div className="text-center">
            <div className="text-2xl font-bold text-orange-600">{metrics?.completionRate || 0}%</div>
            <div className="text-sm text-gray-600">Completion Rate</div>
          </div>
        </div>
      </div>

      {/* Top Performers */}
      <div className="bg-white rounded-lg shadow-md p-6">
        <h3 className="text-xl font-bold mb-4">🎯 Top Performers</h3>
        <div className="space-y-3">
          {performance?.topPerformers?.map((track, index) => (
            <div key={track.track_id} className="flex justify-between items-center p-3 bg-gray-50 rounded-lg">
              <div>
                <div className="font-semibold">{index + 1}. {track.title}</div>
                <div className="text-sm text-gray-600">
                  Viral Score: {track.viralScore} • 
                  Streams: {track.streams?.toLocaleString() || 0} • 
                  Revenue: ${track.revenue || 0}
                </div>
                <div className="text-xs text-blue-600">
                  Sync: {track.syncCategories?.slice(0, 2).join(', ') || 'None'}
                </div>
              </div>
              <div className="text-right">
                <div className="text-lg font-bold text-green-600">{(track.viralScore * 100).toFixed(0)}%</div>
                <div className="text-xs text-gray-500">Viral Potential</div>
              </div>
            </div>
          )) || <div className="text-gray-500">No performance data available</div>}
        </div>
      </div>

      {/* Marketing Insights */}
      <div className="bg-white rounded-lg shadow-md p-6">
        <h3 className="text-xl font-bold mb-4">🎪 Marketing Insights</h3>
        <div className="grid md:grid-cols-2 gap-6">
          
          {/* Pitch Themes */}
          <div>
            <h4 className="font-semibold mb-2">Top Pitch Themes</h4>
            <div className="space-y-2">
              {marketing?.pitchThemes?.slice(0, 5).map(([theme, count], index) => (
                <div key={theme} className="flex justify-between items-center">
                  <span className="text-sm">{theme}</span>
                  <span className="bg-blue-100 text-blue-800 px-2 py-1 rounded text-xs">
                    {count} tracks
                  </span>
                </div>
              )) || <div className="text-gray-500">No pitch themes available</div>}
            </div>
          </div>

          {/* Brand Opportunities */}
          <div>
            <h4 className="font-semibold mb-2">Brand Alignment</h4>
            <div className="space-y-2">
              {Object.entries(marketing?.brandOpportunities || {}).map(([brand, count]) => (
                <div key={brand} className="flex justify-between items-center">
                  <span className="text-sm capitalize">{brand}</span>
                  <div className="flex items-center">
                    <div className="w-16 bg-gray-200 rounded-full h-2 mr-2">
                      <div 
                        className="bg-green-500 h-2 rounded-full" 
                        style={{width: `${Math.min(100, (count / metrics?.totalTracks) * 100)}%`}}
                      ></div>
                    </div>
                    <span className="text-xs text-gray-600">{count}</span>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>

      {/* Sync Categories */}
      <div className="bg-white rounded-lg shadow-md p-6">
        <h3 className="text-xl font-bold mb-4">🎬 Sync Opportunities</h3>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          {marketing?.syncCategories?.slice(0, 8).map(([category, count]) => (
            <div key={category} className="bg-gradient-to-r from-purple-100 to-pink-100 p-3 rounded-lg text-center">
              <div className="font-semibold text-sm">{category}</div>
              <div className="text-xs text-gray-600">{count} tracks</div>
            </div>
          )) || <div className="text-gray-500 col-span-4">No sync categories available</div>}
        </div>
      </div>

      {/* Optimal Posting Times */}
      <div className="bg-white rounded-lg shadow-md p-6">
        <h3 className="text-xl font-bold mb-4">⏰ Optimal Posting Times</h3>
        <div className="flex flex-wrap gap-3">
          {Object.entries(marketing?.optimalPostingTimes || {}).map(([time, count]) => (
            <div key={time} className="bg-blue-100 text-blue-800 px-4 py-2 rounded-full">
              <span className="font-semibold">{time}</span>
              <span className="ml-2 text-sm">({count} tracks)</span>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

export default CatalogAnalytics;
